// Stocktake / audit-reconciliation wire contracts — the session → scan → alert
// pipeline that db/migrations/0007_serials_transit.sql backs (lib_audit_session
// + lib_stocktake_scan + lib_audit_alert). Browser-safe zod only.
//
// BUILT FRESH: the source audit_session/audit_log/audit_alerts tables were
// schema-only (zero service/route/consumer); CLAUDE.md §3.4's auto-reconcil-
// iation ("auditing a Checked Out / Lost copy checks it in, pauses replacement
// fees, logs alerts") existed only in docs, and the three alert_type strings
// lived only in a schema comment. "Trust code over docs" ⇒ the pipeline is
// UNBUILT — these contracts are derived from the spec's columns + vocabularies,
// NOT ported. The reconciliation logic (which emits alerts) is the api/service
// lane's to build; this lane ships the shapes it produces/consumes.
//
// Contract law:
//   · Copy references re-key to the lib_copy SURROGATE (copyId) — the source
//     kept copy_barcode (L0 law); the barcode is join-resolved for display (the
//     transit identity law: opaque id + JOIN-for-display).
//   · The added staff actors (audit_session.created_by, stocktake_scan.
//     scanned_by) are opaque userIds, nullable, and — unlike transit's
//     storage-only actors — VISIBLE on these build-fresh DTOs (accountability is
//     the whole point of a stocktake). No name join (staff live behind the staff
//     seam — the issued_by opacity).
//   · Vocabularies (session status, verification, alert type) ride CLOSED enums
//     — build-fresh controlled values, no legacy stored text to accommodate.
//   · Lists ride CursorQuery (the L4/L5 bounded-list law); spines land in 0007.
import { z } from "zod";
import { CursorQuery, EntityId, EpochMs } from "@erp/core-schemas";

/* ────────────────────────────── vocabularies ────────────────────────────── */

export const AUDIT_SESSION_STATUSES = ["Active", "Completed"] as const;
export const AuditSessionStatus = z.enum(AUDIT_SESSION_STATUSES);
export type AuditSessionStatus = z.infer<typeof AuditSessionStatus>;

/** A scan is Verified (copy on its expected shelf) or Misplaced (found elsewhere). */
export const VERIFICATION_STATUSES = ["Verified", "Misplaced"] as const;
export const VerificationStatus = z.enum(VERIFICATION_STATUSES);
export type VerificationStatus = z.infer<typeof VerificationStatus>;

/**
 * The three reconciliation alert types (pinned — the strings that lived ONLY in
 * the source schema comment, never in code): a copy found on the shelf that the
 * system believes is on LOAN, found on the shelf that the system believes is
 * LOST, or found on the wrong shelf (MISPLACED).
 */
export const ALERT_TYPES = ["FOUND_ON_SHELF_BUT_LOANED", "FOUND_ON_SHELF_BUT_LOST", "MISPLACED"] as const;
export const AlertType = z.enum(ALERT_TYPES);
export type AlertType = z.infer<typeof AlertType>;

/* ─────────────────────────────── sessions ─────────────────────────────── */

/**
 * GET /stocktake/sessions row. scopeZoneId ← scope_zone_id (NULL = whole
 * library); scopeZoneName is the live lib_zone join. createdBy is the opaque
 * staff actor (nullable — the added-audit-column precedent).
 */
export const AuditSession = z.object({
  id: EntityId,
  name: z.string(),
  scopeZoneId: EntityId.nullable(),
  scopeZoneName: z.string().nullable(), // live join (lib_zone.name)
  status: AuditSessionStatus,
  createdBy: EntityId.nullable(), // opaque staff userId — no name join (staff seam)
  createdAt: EpochMs,
  completedAt: EpochMs.nullable(),
});
export type AuditSession = z.infer<typeof AuditSession>;

/**
 * POST /stocktake/sessions. scopeZoneId is an optional body-ref → lib_zone
 * (missing zone ⇒ platform BAD_REQUEST, the body-ref law). created_by is
 * session-stamped (the issuedBy precedent — not a body field). Born 'Active'.
 */
export const CreateAuditSession = z.object({
  name: z.string().trim().min(1).max(200),
  scopeZoneId: EntityId.optional(),
});
export type CreateAuditSession = z.infer<typeof CreateAuditSession>;

/** Keyset list on (created_at DESC, id) + the ?status exact-eq filter. */
export const AuditSessionListQuery = CursorQuery.extend({
  status: AuditSessionStatus.optional(),
});
export type AuditSessionListQuery = z.infer<typeof AuditSessionListQuery>;

/* ──────────────────────────────── scans ──────────────────────────────── */

/**
 * GET /stocktake/sessions/:id/scans row. copyId ← the surrogate; copyBarcode is
 * the live lib_copy join. verificationStatus is DERIVED by the service (Verified
 * when scanned_shelf_id matches the copy's assigned shelf, else Misplaced).
 * scannedBy is the opaque staff actor (nullable).
 */
export const StocktakeScan = z.object({
  id: EntityId,
  auditSessionId: EntityId,
  copyId: EntityId,
  copyBarcode: z.string().nullable(), // live join (lib_copy.barcode)
  scannedShelfId: EntityId,
  verificationStatus: VerificationStatus,
  scannedBy: EntityId.nullable(), // opaque staff userId — no name join (staff seam)
  scannedAt: EpochMs,
});
export type StocktakeScan = z.infer<typeof StocktakeScan>;

/**
 * POST /stocktake/sessions/:id/scans — record a sighting. barcode is the device
 * scan (resolved to copy_id server-side — the transit/issue precedent); shelfId
 * is where the copy was found. The service derives verification_status, stamps
 * scanned_by, and runs the auto-reconciliation (emitting alerts for on-shelf
 * loaned/lost copies). Unknown barcode / shelf ⇒ platform BAD_REQUEST (body-ref).
 */
export const RecordScanBody = z.object({
  barcode: z.string().trim().min(1),
  shelfId: EntityId,
});
export type RecordScanBody = z.infer<typeof RecordScanBody>;

/** Keyset list on (scanned_at DESC, id), scoped to one session. */
export const StocktakeScanListQuery = CursorQuery;
export type StocktakeScanListQuery = z.infer<typeof StocktakeScanListQuery>;

/* ──────────────────────────────── alerts ─────────────────────────────── */

/**
 * GET /stocktake/sessions/:id/alerts row — the reconciliation output. copyId ←
 * the surrogate; copyBarcode is the live join. resolved is the 0/1 flag
 * (bool on the wire — the drm/active INT-flag precedent).
 */
export const AuditAlert = z.object({
  id: EntityId,
  auditSessionId: EntityId,
  copyId: EntityId,
  copyBarcode: z.string().nullable(), // live join (lib_copy.barcode)
  alertType: AlertType,
  details: z.string().nullable(),
  resolved: z.boolean(),
  loggedAt: EpochMs,
});
export type AuditAlert = z.infer<typeof AuditAlert>;

/** POST /stocktake/alerts/:id/resolve — flip the resolved flag (default true; false reopens). */
export const ResolveAlertBody = z.object({
  resolved: z.boolean().default(true),
});
export type ResolveAlertBody = z.infer<typeof ResolveAlertBody>;

/** Keyset list on (logged_at DESC, id) + the ?resolved exact-eq filter (the open-alerts view). */
export const AuditAlertListQuery = CursorQuery.extend({
  resolved: z.stringbool().optional(),
});
export type AuditAlertListQuery = z.infer<typeof AuditAlertListQuery>;
